import React from 'react';
import { Link } from 'react-router-dom';

import { MagnifyingGlassIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import type { ColumnDef } from '@oysterjs/ui/data-table';
import { DataTable } from '@oysterjs/ui/data-table';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Radio, RadioGroup } from '@oysterjs/ui/radio';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  ClientMatchVerdict,
  useGetWorkspaceClientsQuery,
  useLinkClientWorkspaceMutation,
  useSearchClientsQuery,
  useSuggestedClientsForWorkspaceQuery,
  useUnlinkClientWorkspaceMutation
} from '~/api/graphql';
import { messageOfFailure } from '@oysterjs/core/graphql/errors';
import { ClientFormDialog } from '~/components/client-form';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { FormError } from '~/components/form-error';
import { useDebouncedValue } from '~/hooks/use-list-controls';

/** Best few. A longer list is a search, and the search box is right there. */
const SUGGESTION_LIMIT = 3;
const SEARCH_PAGE_SIZE = 10;

const LINK_FAILED = 'Could not file this workspace.';
const UNLINK_FAILED = 'Could not remove this workspace from that client.';

/**
 * How sure the matcher is, in its own words. AMBIGUOUS is shown rather than
 * hidden: a person decides, and one weak candidate beats an empty picker.
 */
const VERDICT_LABELS: Record<ClientMatchVerdict, string> = {
  [ClientMatchVerdict.Exact]: 'Exact match',
  [ClientMatchVerdict.Likely]: 'Likely match',
  [ClientMatchVerdict.Ambiguous]: 'Possible match'
};

const VERDICT_COLORS: Record<ClientMatchVerdict, React.ComponentProps<typeof Badge>['color']> = {
  [ClientMatchVerdict.Exact]: 'green',
  [ClientMatchVerdict.Likely]: 'blue',
  [ClientMatchVerdict.Ambiguous]: 'zinc'
};

/**
 * Chooses the client a workspace belongs to: the matcher's suggestions, then a
 * search, then a new client pre-filled from the search box.
 *
 * Only certain matches link on their own, so what reaches this picker is a
 * person's call. A wrong association buries the workspace under another
 * client's history, where nobody thinks to look for it.
 */
/** Both sources agree on these fields; the rest differ. */
type PickableClient = { id: string; name: string; email?: string | null; phone?: string | null };

/**
 * Radio rather than a button per row: the row is a choice, not an action, and
 * the confirm lives once in the footer. Factory because the match column closes
 * over the suggestions, which only exist when nothing has been searched.
 */
const getClientPickerColumns = (
  verdictOf: (clientID: string) => ClientMatchVerdict | undefined
): ColumnDef<PickableClient>[] => [
  {
    id: 'choose',
    header: '',
    // The row's click handler already selects, so the cell needs no link.
    noHref: true,
    cellClassName: 'w-8 py-2 pl-3 pr-0',
    renderCell: (client) => <Radio value={client.id} color="sky" aria-label={client.name} />
  },
  {
    id: 'client',
    header: 'Client',
    renderCell: (client) => {
      const detail = [client.email, client.phone].filter(Boolean).join(' \u00b7 ');
      return (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {client.name}
          </p>
          {detail && (
            <p className="mt-0.5 truncate text-xs text-neutral-500 dark:text-neutral-400">
              {detail}
            </p>
          )}
        </div>
      );
    }
  },
  {
    id: 'match',
    header: 'Match',
    renderCell: (client) => {
      const verdict = verdictOf(client.id);
      return verdict ? (
        <Badge color={VERDICT_COLORS[verdict]}>{VERDICT_LABELS[verdict]}</Badge>
      ) : null;
    }
  }
];

/**
 * Picks a client from the workspace's suggestions, a search, or a new one.
 * onChoose answers with an error message, or null.
 */
export const ChooseClientDialog = ({
  open,
  workspaceId,
  title,
  description,
  confirmLabel,
  excludeClientIds = [],
  suggest = true,
  onChoose,
  onClose
}: {
  open: boolean;
  /** Whose matcher supplies the suggestions. */
  workspaceId: string;
  title: string;
  description: string;
  confirmLabel: string;
  /** Not offered, such as the clients a document is leaving. */
  excludeClientIds?: string[];
  /** Off for a workspace already filed, whose matcher has nothing left to suggest. */
  suggest?: boolean;
  onChoose: (client: PickableClient) => Promise<string | null>;
  onClose: () => void;
}) => {
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [chosen, setChosen] = React.useState<string | null>(null);
  const [choosing, setChoosing] = React.useState(false);

  const { data: suggested, loading: suggesting } = useSuggestedClientsForWorkspaceQuery({
    variables: { workspaceId, limit: SUGGESTION_LIMIT },
    skip: !open || !suggest
  });

  React.useEffect(() => setChosen(null), [debouncedSearch]);

  const searching = debouncedSearch.trim().length > 0;
  const { data: found, loading: searchLoading } = useSearchClientsQuery({
    variables: { first: SEARCH_PAGE_SIZE, filter: { nameQuery: debouncedSearch.trim() } },
    skip: !open || !searching
  });

  // On close, not on open: the debounce would still hold the last search, so
  // reopening would issue it and discard the result.
  React.useEffect(() => {
    if (!open) {
      setSearch('');
      setError(null);
      setChosen(null);
    }
  }, [open]);

  const choose = async (client: PickableClient) => {
    setError(null);
    setChoosing(true);
    let message: string | null;
    try {
      message = await onChoose(client);
    } catch {
      message = 'Something went wrong. Try again.';
    } finally {
      setChoosing(false);
    }
    if (message) {
      setError(message);
      return;
    }
    onClose();
  };

  const offered = (client: PickableClient) => !excludeClientIds.includes(client.id);
  const suggestions = (suggested?.suggestedClientsForWorkspace ?? []).filter((suggestion) =>
    offered(suggestion.client)
  );
  const results = (found?.clients.edges ?? []).map((edge) => edge.node).filter(offered);
  const loading = searching ? searchLoading : suggesting;
  const rows = searching ? results : suggestions.map((suggestion) => suggestion.client);
  const chosenClient = rows.find((client) => client.id === chosen);
  const verdictOf = (clientId: string) =>
    suggestions.find((suggestion) => suggestion.client.id === clientId)?.verdict;
  const columns = getClientPickerColumns(verdictOf);

  return (
    <>
      <Dialog open={open && !creating} onClose={onClose} size="2xl">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
        <DialogBody>
          <InputGroup>
            <MagnifyingGlassIcon className="h-4 w-4" />
            <Input
              autoFocus
              type="search"
              placeholder="Search clients by name..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </InputGroup>

          <div className="mt-4 min-h-48">
            {loading && rows.length === 0 ? (
              <div className="py-12 text-center">
                <Spinner size="size-6" color="neutral" />
              </div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {searching
                    ? 'No clients match that name.'
                    : !suggest
                      ? 'Search for the client by name, or create it.'
                      : // Empty means the workspace has not run yet, so there is no
                        // extracted name to match on -- not that the book is empty.
                        'No suggestions yet. Search by name, or create the client.'}
                </p>
              </div>
            ) : (
              <>
                {!searching && (
                  <p className="pb-1 text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    Suggested
                  </p>
                )}
                {/* The radios need a RadioGroup ancestor to behave as one
                    control, and the table sits inside it. Its own vertical
                    rhythm is cancelled -- the rows carry their own. */}
                <RadioGroup
                  value={chosen}
                  onChange={setChosen}
                  className="!space-y-0"
                  aria-label="Client"
                >
                  <DataTable
                    data={rows}
                    columns={columns}
                    keyExtractor={(client) => client.id}
                    onRowClick={(client) => setChosen(client.id)}
                    rowClassName="cursor-pointer"
                    tableClassName="[--gutter:theme(spacing.2)]"
                  />
                </RadioGroup>
              </>
            )}
          </div>

          <FormError message={error} className="mt-2 text-sm text-red-600 dark:text-red-400" />
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button outline onClick={() => setCreating(true)}>
            New client
          </Button>
          <Button
            color="sky"
            disabled={!chosenClient || choosing}
            onClick={() => chosenClient && choose(chosenClient)}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pre-filled from the search box: the name they could not find. */}
      <ClientFormDialog
        open={creating}
        initialName={search.trim()}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          void choose(created);
        }}
      />
    </>
  );
};

/**
 * The workspace's clients, and an invitation to add one while it has none.
 *
 * Renders the whole list though policy allows one, so permitting several is a
 * change to that policy rather than to this. Reads the association rather than
 * the suggestions, which go empty once a workspace is filed and so cannot tell
 * "already filed" from "nothing to suggest".
 */
const ClientAssociation = ({
  workspaceId,
  clientName
}: {
  workspaceId: string;
  clientName?: string | null;
}) => {
  const [picking, setPicking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { data, refetch } = useGetWorkspaceClientsQuery({
    variables: { workspaceId },
    fetchPolicy: 'cache-first'
  });

  // The first run that names the client is the one that may have linked it,
  // and the server links before announcing that run, so one re-read suffices.
  const namedOnMount = React.useRef(clientName);
  React.useEffect(() => {
    if (clientName && clientName !== namedOnMount.current) void refetch();
  }, [clientName, refetch]);
  const [unlinkClientWorkspace] = useUnlinkClientWorkspaceMutation();
  const [linkClientWorkspace] = useLinkClientWorkspaceMutation();

  const link = async ({ id: clientId }: { id: string }) => {
    const message = messageOfFailure(
      // false: the picker is only offered while the workspace has no client.
      await linkClientWorkspace({ variables: { clientId, workspaceId, replaceExisting: false } }),
      LINK_FAILED
    );
    if (!message) await refetch();
    return message;
  };

  const clients = data?.caraWorkspace?.clients ?? [];

  // linkClientWorkspace answers with the client, and this reads the workspace's
  // side of the same row, so the association is re-read rather than merged.
  const unlink = async (clientId: string) => {
    setError(null);
    const message = messageOfFailure(
      await unlinkClientWorkspace({ variables: { clientId, workspaceId } }),
      UNLINK_FAILED
    );
    setError(message);
    if (!message) await refetch();
  };

  return (
    <div className="flex items-center gap-2">
      <FormError message={error} className="text-xs text-red-600 dark:text-red-400" />
      {clients.map((client) => (
        // The top bar spaces every item alike, so the chip's boundary is what
        // says the X belongs to the client and not to the run status beside it.
        <span
          key={client.id}
          className="inline-flex items-center gap-1 rounded py-0.5 pl-2 pr-1 text-sm ring-1 ring-neutral-200 dark:ring-neutral-700"
        >
          <UserGroupIcon className="size-3.5 shrink-0 text-neutral-500 dark:text-neutral-400" />
          <Link
            to={`/app/clients/${client.id}`}
            className="max-w-40 truncate text-neutral-700 hover:text-neutral-900 hover:underline dark:text-neutral-300 dark:hover:text-white"
          >
            {client.name}
          </Link>
          {/* Unfiles the workspace; deletes nothing. A trash can would promise
              otherwise, and the one in the overflow menu archives. */}
          <button
            type="button"
            aria-label={`Remove ${client.name} from this workspace`}
            className="rounded p-0.5 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-200 hover:text-neutral-800 dark:hover:bg-white/10 dark:hover:text-neutral-200"
            onClick={() => unlink(client.id)}
          >
            <XMarkIcon className="size-3.5" />
          </button>
        </span>
      ))}
      {/* Offered only while the workspace has none, which is the current policy
          rather than anything this component depends on. */}
      {clients.length === 0 && (
        <Button plain onClick={() => setPicking(true)}>
          <UserGroupIcon className="size-4" />
          Add client
        </Button>
      )}

      <ChooseClientDialog
        open={picking}
        workspaceId={workspaceId}
        title="File this workspace under a client"
        description="Its documents start appearing on that client, alongside the rest of their work."
        confirmLabel="Add client"
        onChoose={link}
        onClose={() => setPicking(false)}
      />
    </div>
  );
};

/**
 * Gates the association control on the clients flag. Separate from
 * ClientAssociation so the flag leaves the query unmounted -- the top bar
 * renders on every workspace and must not issue one when the feature is off.
 */
export const WorkspaceClientPicker = ({
  workspaceId,
  clientName
}: {
  workspaceId: string;
  clientName?: string | null;
}) =>
  useFlag(FEATURE_FLAGS.SHOW_CLIENTS_TAB) ? (
    <ClientAssociation workspaceId={workspaceId} clientName={clientName} />
  ) : null;
